import { z } from 'zod'

import { ConceptChipAchievementWireSchema, toConceptChipAchievement } from '~/@entities/Achievement'
import {
  ConceptChipCountByTypeWireSchema,
  ConceptChipWireSchema,
  toConceptChip,
  toConceptChipCountByType,
} from '~/@entities/ConceptChip'
import type {
  CommonResponseListMaterialViewElementDto,
  CommonResponseListStudentSummaryGroupDto,
  CommonResponseMaterialViewDto,
  CommonResponseRecentlyChallengeAndLittleChapterSummaryDto,
  CommonResponseStudentHomeWeeklyDto,
  CommonResponseStudentStrengthsAndWeaknessesResponse,
  StudentHomeLittleChapterChallengeResponse,
  StudentHomeRecentlyChallengeResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  type AssignedChallenge,
  AssignedChallengeSchema,
  type RecentChallenge,
  type RecentChallengeCurriculum,
  RecentChallengeCurriculumSchema,
  RecentChallengeItemSchema,
  RecentChallengeSchema,
  type RecentStudies,
  RecentStudiesSchema,
  type RecentStudyItem,
  RecentStudyItemSchema,
  type StrengthsAndWeaknesses,
  type WeeklyInfo,
  WeeklyInfoSchema,
  type WeeklyLearningSummary,
  WeeklyLearningSummarySchema,
} from './schemas'

const ENTITY = 'StudentHome'

// 학생 홈 요약은 유형칩 성취도를 코드(0~5|null, A/B/C)로, 칩 종류별 수를 conceptChipA~C 로 낸다.
// 와이어 스키마는 그 조각을 품고, 도메인 어휘로 푸는 일은 검증 뒤 각 엔티티의 to 함수가 한다.
// 생성 타입은 Kotlin nullable 필드(revision·semester·school·studentWorksheetId 등)를 optional 로 그리고 커서의
// lastAssignedCasId 를 빠뜨린다. 스키마가 실제 와이어를 검증하므로 parseApiResponse 에 그대로 넘긴다.

const WeeklyInfoWireSchema = WeeklyInfoSchema.extend({
  bestIncreasedGrade: z.array(ConceptChipAchievementWireSchema),
  bestGradeAchieved: z.array(ConceptChipAchievementWireSchema),
})

export const adaptWeeklyInfo = (raw: CommonResponseStudentHomeWeeklyDto['data']): WeeklyInfo => {
  const wire = parseApiResponse(WeeklyInfoWireSchema, raw, ENTITY)
  return {
    ...wire,
    bestIncreasedGrade: (wire.bestIncreasedGrade ?? []).map(toConceptChipAchievement),
    bestGradeAchieved: (wire.bestGradeAchieved ?? []).map(toConceptChipAchievement),
  }
}

export const adaptRecentStudies = (raw: CommonResponseMaterialViewDto['data']): RecentStudies =>
  parseApiResponse(RecentStudiesSchema, raw, ENTITY)

export const adaptRecentStudyItems = (
  raw: CommonResponseListMaterialViewElementDto['data'],
): RecentStudyItem[] => parseApiResponse(z.array(RecentStudyItemSchema), raw, ENTITY)

const RecentChallengeWireSchema = RecentChallengeSchema.extend({
  contents: z.array(
    RecentChallengeItemSchema.extend({
      learningConceptChip: ConceptChipCountByTypeWireSchema,
      conceptChips: z.array(ConceptChipAchievementWireSchema),
    }),
  ),
})

/** 챌린지 이력이 없으면 서버가 본문을 null 로 낸다 */
export const adaptRecentChallenge = (
  raw: CommonResponseRecentlyChallengeAndLittleChapterSummaryDto['data'],
): RecentChallenge | null => {
  const wire = parseApiResponse(RecentChallengeWireSchema.nullable(), raw, ENTITY)
  if (!wire) return null
  return {
    ...wire,
    contents: (wire.contents ?? []).map((item) => ({
      ...item,
      learningConceptChip: toConceptChipCountByType(item.learningConceptChip),
      conceptChips: (item.conceptChips ?? []).map(toConceptChipAchievement),
    })),
  }
}

const StrengthsAndWeaknessesWireSchema = z.object({
  strengths: ConceptChipAchievementWireSchema.nullable(),
  weaknesses: ConceptChipAchievementWireSchema.nullable(),
})

export const adaptStrengthsAndWeaknesses = (
  raw: CommonResponseStudentStrengthsAndWeaknessesResponse['data'],
): StrengthsAndWeaknesses => {
  const wire = parseApiResponse(StrengthsAndWeaknessesWireSchema, raw, ENTITY)
  return {
    strengths: wire?.strengths ? toConceptChipAchievement(wire.strengths) : null,
    weaknesses: wire?.weaknesses ? toConceptChipAchievement(wire.weaknesses) : null,
  }
}

const AssignedChallengeWireSchema = AssignedChallengeSchema.extend({
  learningConceptChip: ConceptChipCountByTypeWireSchema,
})

export const adaptAssignedChallenge = (
  raw: StudentHomeLittleChapterChallengeResponse,
): AssignedChallenge => {
  const wire = parseApiResponse(AssignedChallengeWireSchema, raw, ENTITY)
  return { ...wire, learningConceptChip: toConceptChipCountByType(wire.learningConceptChip) }
}

const RecentChallengeCurriculumWireSchema = RecentChallengeCurriculumSchema.extend({
  littleChapterConceptChips: z.array(
    RecentChallengeCurriculumSchema.shape.littleChapterConceptChips.element.extend({
      conceptChips: z.array(ConceptChipWireSchema),
    }),
  ),
})

export const adaptRecentChallengeCurriculum = (
  raw: StudentHomeRecentlyChallengeResponse,
): RecentChallengeCurriculum => {
  const wire = parseApiResponse(RecentChallengeCurriculumWireSchema, raw, ENTITY)
  return {
    ...wire,
    littleChapterConceptChips: (wire.littleChapterConceptChips ?? []).map((chapter) => ({
      ...chapter,
      conceptChips: (chapter.conceptChips ?? []).map(toConceptChip),
    })),
  }
}

export const adaptWeeklyLearningSummaries = (
  raw: CommonResponseListStudentSummaryGroupDto['data'],
): WeeklyLearningSummary[] => parseApiResponse(z.array(WeeklyLearningSummarySchema), raw, ENTITY)
